/**
 * Serge Slideshow
 * Touch-enabled slideshow with autoplay, keyboard navigation, and accessibility
 */
(function() {
  'use strict';

  class SergeSlideshow {
    constructor(container) {
      this.container = container;
      this.track = container.querySelector('[data-slideshow-track]');
      this.slides = container.querySelectorAll('[data-slide]');
      this.prevBtn = container.querySelector('[data-slideshow-prev]');
      this.nextBtn = container.querySelector('[data-slideshow-next]');
      this.dots = container.querySelectorAll('[data-slideshow-dot]');

      this.currentIndex = 0;
      this.slideCount = this.slides.length;
      this.autoplay = container.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(container.dataset.autoplaySpeed) * 1000 || 5000;
      this.autoplayTimer = null;
      this.isTransitioning = false;

      // Touch tracking
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.minSwipeDistance = 50;

      // Check for reduced motion preference
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.slideCount > 1) {
        this.init();
      }
    }

    init() {
      this.bindEvents();

      if (this.autoplay && !this.reducedMotion) {
        this.startAutoplay();
      }
    }

    bindEvents() {
      // Arrow navigation
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Dot navigation
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.slideshowDot);
          this.goTo(index);
        });
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      });

      // Touch events
      this.container.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.container.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

      // Pause autoplay on hover/focus
      if (this.autoplay) {
        this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.container.addEventListener('mouseleave', () => this.startAutoplay());
        this.container.addEventListener('focusin', () => this.pauseAutoplay());
        this.container.addEventListener('focusout', () => this.startAutoplay());
      }

      // Handle visibility change
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else if (this.autoplay) {
          this.startAutoplay();
        }
      });

      // Handle reduced motion preference change
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this.reducedMotion = e.matches;
        if (this.reducedMotion) {
          this.pauseAutoplay();
        } else if (this.autoplay) {
          this.startAutoplay();
        }
      });
    }

    goTo(index) {
      if (this.isTransitioning || index === this.currentIndex) return;
      if (index < 0 || index >= this.slideCount) return;

      this.isTransitioning = true;

      // Update track position
      const offset = index * -100;
      this.track.style.transform = `translateX(${offset}%)`;

      // Update active states
      this.slides[this.currentIndex].classList.remove('is-active');
      this.slides[this.currentIndex].setAttribute('aria-hidden', 'true');
      this.slides[index].classList.add('is-active');
      this.slides[index].setAttribute('aria-hidden', 'false');

      // Update dots
      if (this.dots.length) {
        this.dots[this.currentIndex].classList.remove('is-active');
        this.dots[this.currentIndex].setAttribute('aria-selected', 'false');
        this.dots[index].classList.add('is-active');
        this.dots[index].setAttribute('aria-selected', 'true');
      }

      this.currentIndex = index;

      // Reset autoplay timer
      if (this.autoplay && !this.reducedMotion) {
        this.startAutoplay();
      }

      // Allow next transition after animation
      setTimeout(() => {
        this.isTransitioning = false;
      }, 600);
    }

    prev() {
      const newIndex = this.currentIndex === 0 ? this.slideCount - 1 : this.currentIndex - 1;
      this.goTo(newIndex);
    }

    next() {
      const newIndex = this.currentIndex === this.slideCount - 1 ? 0 : this.currentIndex + 1;
      this.goTo(newIndex);
    }

    startAutoplay() {
      this.pauseAutoplay();
      if (!this.reducedMotion) {
        this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
      }
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }

    handleSwipe() {
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > this.minSwipeDistance) {
        if (diff > 0) {
          // Swipe left - next slide
          this.next();
        } else {
          // Swipe right - previous slide
          this.prev();
        }
      }
    }
  }

  // Initialize all slideshows on page
  function initSlideshows() {
    document.querySelectorAll('[data-slideshow]').forEach((el) => {
      new SergeSlideshow(el);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSlideshows);
  } else {
    initSlideshows();
  }

  // Re-initialize on Shopify section load (Theme Editor)
  document.addEventListener('shopify:section:load', (e) => {
    const slideshow = e.target.querySelector('[data-slideshow]');
    if (slideshow) {
      new SergeSlideshow(slideshow);
    }
  });
})();
